import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, Edit, Check, X, AlertTriangle, CheckCircle, Upload, Download, Search, UserX, UserCheck } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminGuestList = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkResults, setCheckResults] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [config, setConfig] = useState({ enforce_guest_list: false, notify_on_excess: true });
  
  const [formData, setFormData] = useState({
    family_name: '',
    first_name: '',
    email: '',
    max_adults: 1,
    max_children: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [guestsRes, configRes] = await Promise.all([
        api.get('/admin/guest-list'),
        api.get('/admin/guest-list/config')
      ]);
      setGuests(guestsRes.data.guests || []);
      setConfig(configRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGuest) {
        await api.put(`/admin/guest-list/${editingGuest.id}`, formData);
        toast.success('Invité mis à jour');
      } else {
        await api.post('/admin/guest-list', formData);
        toast.success('Invité ajouté');
      }
      setShowAddDialog(false);
      setEditingGuest(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (guestId) => {
    if (!confirm('Supprimer cet invité de la liste ?')) return;
    try {
      await api.delete(`/admin/guest-list/${guestId}`);
      toast.success('Invité supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCheckRSVPs = async () => {
    setCheckLoading(true);
    try {
      const res = await api.get('/admin/guest-list/check-rsvps');
      setCheckResults(res.data);
      if (res.data.flagged_count === 0 && res.data.not_in_list_count === 0) {
        toast.success('Toutes les confirmations sont conformes !');
      } else {
        toast.warning(`${res.data.flagged_count + res.data.not_in_list_count} problème(s) détecté(s)`);
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setCheckLoading(false);
    }
  };

  const handleImportFromRSVPs = async () => {
    setImportLoading(true);
    try {
      const res = await api.post('/admin/guest-list/import-from-rsvps');
      toast.success(`${res.data.imported} invité(s) ajouté(s)${res.data.skipped ? `, ${res.data.skipped} déjà présent(s)` : ''}`);
      setShowImportDialog(false);
      loadData();
      setCheckResults(null);
    } catch (error) {
      toast.error("Erreur lors de l'import des RSVP");
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfigChange = async (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    try {
      await api.put('/admin/guest-list/config', newConfig);
      toast.success('Configuration mise à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      family_name: '',
      first_name: '',
      email: '',
      max_adults: 1,
      max_children: 0,
      notes: ''
    });
  };

  const openEditDialog = (guest) => {
    setEditingGuest(guest);
    setFormData({
      family_name: guest.family_name || '',
      first_name: guest.first_name || '',
      email: guest.email || '',
      max_adults: guest.max_adults || 1,
      max_children: guest.max_children || 0,
      notes: guest.notes || ''
    });
    setShowAddDialog(true);
  };

  const filteredGuests = guests.filter(g => {
    const term = searchTerm.toLowerCase();
    return (
      g.family_name?.toLowerCase().includes(term) ||
      g.first_name?.toLowerCase().includes(term) ||
      g.email?.toLowerCase().includes(term)
    );
  });

  const totalAuthorizedAdults = guests.reduce((sum, g) => sum + (g.max_adults || 1), 0);
  const totalAuthorizedChildren = guests.reduce((sum, g) => sum + (g.max_children || 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-charcoal" data-testid="guest-list-title">
              Liste des invités autorisés
            </h1>
            <p className="text-stone mt-1">Gérez qui peut confirmer sa présence et contrôlez les +1</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              className="gap-2"
              data-testid="import-rsvps-btn"
            >
              <Upload className="w-4 h-4" />
              Importer depuis les RSVP
            </Button>
            <Button
              variant="outline"
              onClick={handleCheckRSVPs}
              disabled={checkLoading}
              className="gap-2"
              data-testid="check-rsvps-btn"
            >
              <AlertTriangle className="w-4 h-4" />
              {checkLoading ? 'Vérification...' : 'Vérifier les RSVP'}
            </Button>
            <Button
              onClick={() => { resetForm(); setEditingGuest(null); setShowAddDialog(true); }}
              className="gap-2 bg-terracotta hover:bg-terracotta/90"
              data-testid="add-guest-btn"
            >
              <Plus className="w-4 h-4" />
              Ajouter un invité
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-charcoal">{guests.length}</div>
              <p className="text-sm text-stone">Invités dans la liste</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">{totalAuthorizedAdults}</div>
              <p className="text-sm text-stone">Adultes autorisés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{totalAuthorizedChildren}</div>
              <p className="text-sm text-stone">Enfants autorisés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-terracotta">{totalAuthorizedAdults + totalAuthorizedChildren}</div>
              <p className="text-sm text-stone">Total places</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="list" className="space-y-4">
          <TabsList>
            <TabsTrigger value="list">Liste des invités</TabsTrigger>
            <TabsTrigger value="check">Vérification des RSVP</TabsTrigger>
            <TabsTrigger value="settings">Paramètres</TabsTrigger>
          </TabsList>

          {/* Liste des invités */}
          <TabsContent value="list" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone" />
                <Input
                  placeholder="Rechercher un invité..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-guest-input"
                />
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bone/50">
                      <tr>
                        <th className="text-left p-4 font-medium text-charcoal">Nom</th>
                        <th className="text-left p-4 font-medium text-charcoal">Email</th>
                        <th className="text-center p-4 font-medium text-charcoal">Adultes max</th>
                        <th className="text-center p-4 font-medium text-charcoal">Enfants max</th>
                        <th className="text-left p-4 font-medium text-charcoal">Notes</th>
                        <th className="text-right p-4 font-medium text-charcoal">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-light">
                      {filteredGuests.map((guest) => (
                        <tr key={guest.id} className="hover:bg-bone/30">
                          <td className="p-4">
                            <div className="font-medium text-charcoal">
                              {guest.first_name} {guest.family_name}
                            </div>
                          </td>
                          <td className="p-4 text-stone">{guest.email || '-'}</td>
                          <td className="p-4 text-center">
                            <Badge variant="outline">{guest.max_adults}</Badge>
                          </td>
                          <td className="p-4 text-center">
                            <Badge variant="outline" className="bg-green-50">{guest.max_children}</Badge>
                          </td>
                          <td className="p-4 text-stone text-sm max-w-xs truncate">{guest.notes || '-'}</td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(guest)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(guest.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredGuests.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-stone">
                            {searchTerm ? 'Aucun invité trouvé' : 'Aucun invité dans la liste'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vérification des RSVP */}
          <TabsContent value="check" className="space-y-4">
            {!checkResults ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="w-12 h-12 text-stone mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-charcoal mb-2">Vérifier les confirmations</h3>
                  <p className="text-stone mb-4">
                    Comparez les RSVP reçus avec votre liste d'invités autorisés pour détecter les +1 non autorisés.
                  </p>
                  <Button onClick={handleCheckRSVPs} disabled={checkLoading} className="gap-2">
                    <Search className="w-4 h-4" />
                    Lancer la vérification
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Résumé */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="pt-6 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-green-700">{checkResults.ok_count}</div>
                      <p className="text-sm text-green-600">Conformes</p>
                    </CardContent>
                  </Card>
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="pt-6 text-center">
                      <AlertTriangle className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-orange-700">{checkResults.flagged_count}</div>
                      <p className="text-sm text-orange-600">+1 non autorisés</p>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="pt-6 text-center">
                      <UserX className="w-8 h-8 text-red-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-red-700">{checkResults.not_in_list_count}</div>
                      <p className="text-sm text-red-600">Pas dans la liste</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Détails des problèmes */}
                {checkResults.flagged_count > 0 && (
                  <Card className="border-orange-200">
                    <CardHeader>
                      <CardTitle className="text-orange-700 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        +1 non autorisés ({checkResults.flagged_count})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {checkResults.flagged.map((item, idx) => (
                          <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-charcoal">
                                  {item.first_name} {item.family_name}
                                </p>
                                <p className="text-sm text-stone">{item.email}</p>
                              </div>
                              <div className="text-right text-sm">
                                <p>Adultes: {item.actual_adults}/{item.max_adults}</p>
                                <p>Enfants: {item.actual_children}/{item.max_children}</p>
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {item.issues.map((issue, i) => (
                                <Badge key={i} variant="destructive" className="bg-orange-500">
                                  {issue}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {checkResults.not_in_list_count > 0 && (
                  <Card className="border-red-200">
                    <CardHeader>
                      <CardTitle className="text-red-700 flex items-center gap-2">
                        <UserX className="w-5 h-5" />
                        Invités non trouvés dans la liste ({checkResults.not_in_list_count})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {checkResults.not_in_list.map((item, idx) => (
                          <div key={idx} className="p-3 bg-red-50 rounded-lg flex justify-between items-center">
                            <div>
                              <p className="font-medium text-charcoal">
                                {item.first_name} {item.family_name}
                              </p>
                              <p className="text-sm text-stone">{item.email}</p>
                            </div>
                            <div className="text-right text-sm text-stone">
                              {item.adults} adulte(s), {item.children} enfant(s)
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Button variant="outline" onClick={handleCheckRSVPs} className="gap-2">
                  <Search className="w-4 h-4" />
                  Relancer la vérification
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Paramètres */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres de contrôle</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-bone/50 rounded-lg">
                  <div>
                    <p className="font-medium text-charcoal">Notification automatique</p>
                    <p className="text-sm text-stone">
                      Recevoir une alerte quand un invité dépasse son quota
                    </p>
                  </div>
                  <Switch
                    checked={config.notify_on_excess}
                    onCheckedChange={(v) => handleConfigChange('notify_on_excess', v)}
                  />
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Note :</strong> La liste d'invités autorisés est utilisée pour vérifier les confirmations a posteriori. 
                    Elle ne bloque pas automatiquement les inscriptions, mais vous permet de détecter facilement les +1 non prévus.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog Ajout/Modification */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingGuest ? 'Modifier l\'invité' : 'Ajouter un invité'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="guest-first-name-input"
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.family_name}
                  onChange={(e) => setFormData({ ...formData, family_name: e.target.value })}
                  required
                  data-testid="guest-family-name-input"
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="guest-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Adultes autorisés</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={formData.max_adults}
                  onChange={(e) => setFormData({ ...formData, max_adults: parseInt(e.target.value) || 1 })}
                  data-testid="guest-max-adults-input"
                />
                <p className="text-xs text-stone mt-1">Incluant l'invité lui-même</p>
              </div>
              <div>
                <Label>Enfants autorisés</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={formData.max_children}
                  onChange={(e) => setFormData({ ...formData, max_children: parseInt(e.target.value) || 0 })}
                  data-testid="guest-max-children-input"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Ex: Famille proche, ami d'enfance..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-terracotta hover:bg-terracotta/90">
                {editingGuest ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import RSVP Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-terracotta" />
              Importer depuis les RSVP
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone">
            Toutes les familles ayant déjà confirmé (RSVP) seront ajoutées à la liste autorisée avec leur nombre d'adultes et d'enfants actuel.
            Les invités déjà présents dans la liste seront ignorés (pas de doublons).
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setShowImportDialog(false)}>Annuler</Button>
            <Button
              className="w-full sm:w-auto bg-terracotta hover:bg-terracotta/90"
              onClick={handleImportFromRSVPs}
              disabled={importLoading}
              data-testid="confirm-import-rsvps-btn"
            >
              {importLoading ? 'Import en cours...' : 'Importer tous les RSVP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminGuestList;
